/**
 * Phone Number Formatting Utilities
 * 
 * Utilities for formatting and validating Australian mobile phone numbers
 * Accepts various input formats and formats them consistently
 */

/**
 * Format Australian mobile phone number to standard format: 04XX XXX XXX
 * Accepts various input formats:
 * - 0412345678 (no spaces)
 * - 0412 345 678 (with spaces)
 * - 0412-345-678 (with dashes)
 * - +61 4 1234 5678 (international format)
 * - 04 1234 5678 (with different spacing)
 * 
 * Formats as user types, allowing partial input
 * 
 * @param phoneNumber - Raw phone number input
 * @returns Formatted phone number in format "04XX XXX XXX" or partial format while typing
 */
export function formatPhoneNumber(phoneNumber: string): string {
  if (!phoneNumber) return '';
  
  // Remove all non-digit characters except leading +
  let cleaned = phoneNumber.replace(/[^\d+]/g, '');
  
  // Handle international format (+61 4...)
  if (cleaned.startsWith('+61')) {
    cleaned = '0' + cleaned.substring(3);
  }
  
  // Remove leading + if present
  cleaned = cleaned.replace(/^\+/, '');
  
  // Remove all non-digits
  cleaned = cleaned.replace(/\D/g, '');
  
  // Must start with 04
  if (!cleaned.startsWith('04')) {
    // If it doesn't start with 04, return as-is (might be partial input)
    return phoneNumber;
  }
  
  // Format based on length
  if (cleaned.length <= 4) {
    // Just the area code: 04XX
    return cleaned;
  } else if (cleaned.length <= 7) {
    // Area code + first 3 digits: 04XX XXX
    return `${cleaned.substring(0, 4)} ${cleaned.substring(4)}`;
  } else if (cleaned.length <= 10) {
    // Full format: 04XX XXX XXX
    return `${cleaned.substring(0, 4)} ${cleaned.substring(4, 7)} ${cleaned.substring(7, 10)}`;
  } else {
    // Too long, truncate to 10 digits and format
    cleaned = cleaned.substring(0, 10);
    return `${cleaned.substring(0, 4)} ${cleaned.substring(4, 7)} ${cleaned.substring(7, 10)}`;
  }
}

/**
 * Validate Australian mobile phone number
 * More forgiving - accepts various formats and validates the actual number
 * 
 * @param phoneNumber - Phone number to validate
 * @returns true if valid Australian mobile number, false otherwise
 */
export function isValidAustralianMobile(phoneNumber: string): boolean {
  if (!phoneNumber || phoneNumber.trim() === '') return true; // Empty is valid (optional field)
  
  // Remove all non-digit characters
  let cleaned = phoneNumber.replace(/\D/g, '');
  
  // Handle international format (+61 4...)
  // After removing non-digits, +61 becomes 61, so we need to check if it starts with 61
  if (cleaned.startsWith('61') && cleaned.length === 11) {
    // International format: +61 4... becomes 614... (11 digits)
    cleaned = '0' + cleaned.substring(2); // Remove 61, add 0
  }
  
  // Must be exactly 10 digits starting with 04
  const mobileRegex = /^04\d{8}$/;
  return mobileRegex.test(cleaned);
}

/**
 * Normalize phone number for storage
 * Converts any valid format to standard format: 04XX XXX XXX
 * 
 * @param phoneNumber - Phone number to normalize
 * @returns Normalized phone number or empty string if invalid
 */
export function normalizePhoneNumber(phoneNumber: string): string {
  if (!phoneNumber || phoneNumber.trim() === '') return '';
  
  const formatted = formatPhoneNumber(phoneNumber);
  
  // If formatting didn't change it (meaning it was invalid), try to validate
  if (formatted === phoneNumber && !isValidAustralianMobile(phoneNumber)) {
    return ''; // Return empty if invalid
  }
  
  return formatted;
}

